import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useVault } from "@/context/VaultContext";
import { sha256Hex } from "@/lib/vaultCrypto";
import { categoryOf, formatIncidentDate, formatRecorded } from "@/lib/vaultMeta";

export default function VaultExport() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { unlocked, entries } = useVault();
  const [fingerprint, setFingerprint] = useState("");

  const ordered = useMemo(() => [...entries].filter((e) => !e.corrupt).sort((a, b) => (a.data.incident_date || "").localeCompare(b.data.incident_date || "") || a.recorded_at.localeCompare(b.recorded_at)), [entries]);

  useEffect(() => {
    if (!unlocked) navigate("/vault", { replace: true });
  }, [unlocked, navigate]);

  useEffect(() => {
    const hashes = [...entries].sort((a, b) => a.recorded_at.localeCompare(b.recorded_at)).map((e) => e.content_hash).join("");
    sha256Hex(hashes).then(setFingerprint);
  }, [entries]);

  if (!unlocked) return null;
  const generated = new Date();

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8" data-testid="vault-export-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 print:hidden">
        <Button variant="ghost" onClick={() => navigate("/vault")} className="gap-2 text-slate-600"><ArrowLeft size={16} /> Back to vault</Button>
        <Button data-testid="vault-print-btn" onClick={() => window.print()} className="bg-slate-900 hover:bg-emerald-600 rounded-full gap-2"><Printer size={16} /> Print / Save as PDF</Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-8 print:border-0 print:p-0 print:rounded-none">
        <header className="border-b-2 border-slate-900 pb-4 mb-6">
          <p className="font-mono text-xs uppercase tracking-widest text-slate-500">Documentation Vault · Chronological log</p>
          <h1 className="font-display text-3xl text-slate-900 mt-1" style={{ fontWeight: 800 }}>Incident record — {user?.name}</h1>
          <div className="text-sm text-slate-600 mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1">
            <span>Generated: {generated.toLocaleString(undefined, { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit", timeZoneName: "short" })}</span>
            <span>Entries: {ordered.length}</span>
            <span className="sm:col-span-2 font-mono text-xs break-all" data-testid="vault-fingerprint">Vault fingerprint: <span data-testid="vault-fingerprint-hex">{fingerprint}</span></span>
          </div>
        </header>

        <ol className="space-y-7" data-testid="vault-export-entries">
          {ordered.map((e, i) => {
            const d = e.data;
            return (
              <li key={e.id} className="break-inside-avoid">
                <div className="flex items-baseline gap-3">
                  <span className="font-mono text-sm text-slate-400">#{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="font-display text-xl text-slate-900" style={{ fontWeight: 700 }}>{d.title || "Untitled entry"}</h2>
                </div>
                <table className="text-sm mt-2 w-full">
                  <tbody>
                    <tr><td className="text-slate-500 pr-4 py-0.5 align-top w-40">Date of incident</td><td className="py-0.5">{formatIncidentDate(d.incident_date)}{d.incident_time ? ` at ${d.incident_time}` : ""}</td></tr>
                    {d.location && <tr><td className="text-slate-500 pr-4 py-0.5 align-top">Location</td><td className="py-0.5">{d.location}</td></tr>}
                    <tr><td className="text-slate-500 pr-4 py-0.5 align-top">Category</td><td className="py-0.5">{categoryOf(d.category).label}</td></tr>
                    {d.individuals?.length > 0 && <tr><td className="text-slate-500 pr-4 py-0.5 align-top">Individuals involved</td><td className="py-0.5">{d.individuals.map((p) => [p.name, p.title].filter(Boolean).join(" — ")).join("; ")}</td></tr>}
                    {d.witnesses?.length > 0 && <tr><td className="text-slate-500 pr-4 py-0.5 align-top">Witnesses</td><td className="py-0.5">{d.witnesses.map((p) => [p.name, p.title].filter(Boolean).join(" — ")).join("; ")}</td></tr>}
                    {d.supervisor_name && <tr><td className="text-slate-500 pr-4 py-0.5 align-top">Related supervisor</td><td className="py-0.5">{d.supervisor_name}</td></tr>}
                    <tr><td className="text-slate-500 pr-4 py-0.5 align-top">Account</td><td className="py-0.5 whitespace-pre-wrap">{d.what_happened}</td></tr>
                    {e.attachments?.length > 0 && <tr><td className="text-slate-500 pr-4 py-0.5 align-top">Attachments</td><td className="py-0.5">{e.attachments.map((a) => `${a.meta.name} (sha256 ${a.content_hash.slice(0, 16)}…)`).join("; ")}</td></tr>}
                  </tbody>
                </table>
                <p className="font-mono text-[11px] text-slate-400 mt-2">
                  Recorded {formatRecorded(e.recorded_at)}{e.revision > 1 ? ` · amended ${formatRecorded(e.updated_at)} (rev ${e.revision})` : ""} · entry hash {e.content_hash}
                </p>
              </li>
            );
          })}
        </ol>

        <footer className="mt-10 pt-4 border-t border-slate-200 text-xs text-slate-500 leading-relaxed">
          <p className="flex items-start gap-2"><ShieldCheck size={14} className="shrink-0 mt-0.5" />
            <span><b>About this record.</b> Each entry was encrypted on the author's device before being stored by my360boss. "Recorded" timestamps and entry hashes (SHA-256 of the encrypted entry) were assigned by the my360boss server at the moment each entry was saved, and cannot be altered by the author afterwards without producing a new hash and revision number. my360boss cannot read entry contents and makes no representation about their accuracy; this document is the author's own account.</span>
          </p>
        </footer>
      </div>
    </div>
  );
}
